/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import i18n from 'i18next';

// Component imports
import { Select, Skeleton, Space, Typography } from 'antd';
import { StarOutlined } from '@ant-design/icons';
import MainActivityButton from '../../UI/MainActivityButton';

// Misc imports
import { ACTIVITY_TYPES, CHAPTERSOURCE_SCHEMAS } from '../../../constants/main';
import { DEFAULT_FALLBACK_ID, NODE_TYPES } from '../../../constants/flowGraph';
import { useFetchChapterSourcesQuery, useFetchMainChapterSourceQuery } from '../../../store/chapterSources/chapterSourcesApi';
import { useBotLanguage } from '../../../utils/hooks/useBotLanguage';
import is from '../../../utils/is';

/**
 * Generates a list of available entry points, for use within a Select component
 *
 * @param {Array} chaptersources A list of all chaptersources for finding their intro nodes
 * @param {String} currentSearchValue The current search value of the input, if any, to append to the list
 * @param {Boolean} showActivities Whether to show activities in the list
 * @param {Boolean} showAutomatedAnswers Whether to show automated answers in the list
 * @param {String} currentBotLanguage The current language of the bot, to show the correct name of the parent activity
 * @returns A list of Select options with all available entry points
 */
const generateSuggestions = (chaptersources, currentSearchValue, showActivities, showAutomatedAnswers, currentBotLanguage, primaryBotLanguage, tenantId) => {
  const activities = chaptersources.filter(c => [ACTIVITY_TYPES.DECISION_TREE, ACTIVITY_TYPES.INTERVIEW, ACTIVITY_TYPES.LIVECHAT_FLOW, ACTIVITY_TYPES.USER_FEEDBACK].includes(c.type));
  const automatedAnswers = chaptersources.filter(c => [ACTIVITY_TYPES.AUTOMATED_ANSWERS].includes(c.type));

  let recommendations = [];

  if (showActivities && is.array(activities) && !is.emptyArray(activities)) {
    recommendations = [
      ...recommendations,
      {
        label: i18n.t('Flows'),
        options: activities
          // This is a temporary filter to hide duplicate values (activities containing the same intro node ID)
          // TODO: Remove this when the backend stops allowing the same intro node ID as other activities
          .filter((activity) => {
            const introNode = activity.content.nodes.find(node => node.type === NODE_TYPES.INTRO);
            const isIntroNodeDuplicate = activities
              .filter(act => act.id !== activity.id)
              .some(act => act.content.nodes.find(node => node.type === NODE_TYPES.INTRO)?.id === introNode.id);

            if (isIntroNodeDuplicate) {
              return null;
            } else {
              return activity;
            }
          })
          .map(activity => {
            // Find the intro node of the activity
            const introNode = activity.content.nodes.find(node => node.type === NODE_TYPES.INTRO);

            if (introNode) {
              return {
                value: introNode.id,
                name: activity.name[currentBotLanguage] || activity.name[primaryBotLanguage],
                label: (
                  <Space size={4}>
                    <Typography.Text>
                      <Space size={8}>
                        <MainActivityButton customIndicator={<StarOutlined />} chaptersource={activity} hideIfNotMain botId={tenantId} />
                        {activity.name[currentBotLanguage] || activity.name[primaryBotLanguage]}
                      </Space>
                    </Typography.Text>

                    <Typography.Text type="secondary">
                      ({introNode.id})
                    </Typography.Text>
                  </Space>
                )
              };
            } else {
              return null;
            }
          })
      }
    ];
  }

  if (showAutomatedAnswers && is.array(automatedAnswers) && !is.emptyArray(automatedAnswers)) {
    recommendations = [
      ...recommendations,
      {
        label: i18n.t('Automated answers'),
        options: automatedAnswers.map(automatedAnswer => {
          // Find the intro node of the automated answer
          const introNode = automatedAnswer?.content.nodes.find(node => node.type === NODE_TYPES.INTRO);

          if (introNode) {
            return {
              value: introNode.id,
              name: automatedAnswer.name[currentBotLanguage] || automatedAnswer.name[primaryBotLanguage],
              label: (
                <Space size={4}>
                  <Typography.Text>
                    <Space size={8}>
                      <MainActivityButton customIndicator={<StarOutlined />} chaptersource={automatedAnswer} hideIfNotMain botId={tenantId} />
                      {automatedAnswer.name[currentBotLanguage] || automatedAnswer.name[primaryBotLanguage]}
                    </Space>
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    ({introNode.id})
                  </Typography.Text>
                </Space>
              )
            };
          } else {
            return null;
          }
        })
      }
    ];
  }

  // Allow the user to add values that don't already exist too
  if (currentSearchValue && !recommendations.find(rec => rec.options.some(opt => opt.value === currentSearchValue))) {
    recommendations = [
      ...recommendations,
      {
        label: i18n.t('Other'),
        options: [{
          value: currentSearchValue,
          name: currentSearchValue,
          label: currentSearchValue
        }]
      }
    ];
  }

  return recommendations;
};

/**
 * An AntD Select that displays suggestions for entry points
 */
const EntryPointSelect = ({
  showActivities = true,
  showAutomatedAnswers = true,
  activityTypesToDisplay = [ACTIVITY_TYPES.DECISION_TREE, ACTIVITY_TYPES.INTERVIEW, ACTIVITY_TYPES.LIVECHAT_FLOW, ACTIVITY_TYPES.AUTOMATED_ANSWERS],
  placeholder = 'Choose a flow',
  size = 'default',
  allowClear = false,
  value,
  onChange = () => {},
  onClear = () => {},
  getPopupContainer = (triggerNode) => triggerNode.parentElement, // Fix drop-menu position issues by rendering it in the parent element (override if needed)
  botId = null,
  ...restProps
}) => {
  const { orgId, tenantId } = useParams();
  const { currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const [currentSearchValue, setCurrentSearchValue] = useState(value);
  const [chaptersources, setChaptersources] = useState([]);

  const currentTenantId = botId || tenantId;

  const { data: mainChaptersource, isFetching: isFetchingMainChaptersource } = useFetchMainChapterSourceQuery({ orgId, tenantId: currentTenantId }, { skip: !orgId || !currentTenantId });
  const { data: fetchedChaptersources, isFetching: isFetchingChaptersources, isSuccess } = useFetchChapterSourcesQuery({ orgId, tenantId: currentTenantId, urlParams: {
    schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
    types: activityTypesToDisplay.join()
  } }, { skip: !orgId || !currentTenantId });

  // Not using Redux store for the suggestions, to avoid data overlap with the current store
  const getMainEntryPointId = () => {
    const mainEntryPointId = chaptersources.find(a => a.id === mainChaptersource?.id)?.content?.nodes.find(node => node.type === NODE_TYPES.INTRO)?.id;
    return mainEntryPointId;
  };

  useEffect(() => {
    if (isSuccess) {
      setChaptersources(fetchedChaptersources?.items?.filter(a => a.id !== DEFAULT_FALLBACK_ID) || []);
    }

    // Cleanup
    return () => setChaptersources([]);
  }, [isSuccess, isFetchingChaptersources]);

  // If no value is specified, set default value
  useEffect(() => {
    if (!value && !allowClear && !isFetchingMainChaptersource && !isFetchingChaptersources) {
      const mainEntryPointId = getMainEntryPointId();

      if (mainEntryPointId) {
        // Main activity
        onChange(mainEntryPointId);
      } else if (!is.emptyArray(chaptersources)) {
        // First recommendation in the list
        onChange(generateSuggestions(chaptersources, '', showActivities, showAutomatedAnswers, currentBotLanguage, primaryBotLanguage, currentTenantId)[0]?.options[0]?.value);
      } else if (value === '') {
        // No value fallback
        onChange(undefined);
      }
    }
  }, [chaptersources, mainChaptersource, isFetchingChaptersources, isFetchingMainChaptersource]);

  return isFetchingChaptersources && is.emptyArray(chaptersources) ? (
    <Skeleton.Input active size={size} className="w-100 opacity-70 border-radius-base" />
  ) : (
    <Select
      showSearch
      allowClear
      onClear={onClear}
      placeholder={placeholder}
      size={size}
      value={value}
      onSearch={(val) => setCurrentSearchValue(val)}
      onChange={onChange}
      filterOption={(input, option) => {
        if (option.name) {
          let nameFound = option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          let valueFound = option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          return nameFound ? true : !!valueFound;
        }
      }}
      getPopupContainer={getPopupContainer}
      {...restProps}
    >
      {
        generateSuggestions(chaptersources, currentSearchValue, showActivities, showAutomatedAnswers, currentBotLanguage, primaryBotLanguage, currentTenantId).map(group => (
          <Select.OptGroup key={group.label} label={group.label}>
            {
              group.options.map(opt => (
                <Select.Option
                  key={opt.value}
                  value={opt.value}
                  name={opt.name}
                >
                  {opt.label}
                </Select.Option>
              ))
            }
          </Select.OptGroup>
        ))
      }
    </Select>
  );
};

EntryPointSelect.propTypes = {
  showActivities: PropTypes.bool,
  showAutomatedAnswers: PropTypes.bool,
  allowClear: PropTypes.bool,
  activityTypesToDisplay: PropTypes.array,
  placeholder: PropTypes.string,
  size: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  getPopupContainer: PropTypes.func,
  botId: PropTypes.string
};

export default EntryPointSelect;
